import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Typography,
} from 'antd';
import { BsBox2 } from 'react-icons/bs';
import { MdOutlineLocalOffer } from 'react-icons/md';
import { Link } from 'react-router-dom';

import { formatCurrency } from '../../utils/format';
import FormFooter from './components/FormFooter';
import SectionCard from './components/SectionCard';
import { descriptionProps, val } from './components/descriptions';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text } = Typography;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/** Үнийн талбар: мянгатын таслалтай харагдаж, хасах утга оруулахыг хориглоно */
const currencyInputProps = {
  min: 0,
  style: { width: '100%' },
  formatter: (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
  parser: (value) => `${value ?? ''}`.replace(/[^\d.]/g, ''),
  onKeyDown: (event) => {
    if (event.key === '-') event.preventDefault();
  },
};

/** Өнгөрсөн өдөр сонгуулахгүй — хямдрал үүсмэгцээ дуусахаас сэргийлнэ */
const disabledPastDate = (date) => !!date && date.clone().endOf('day').valueOf() < Date.now();

/**
 * Хямдрал үүсгэх/засах хоёулаа нэг ижил формыг хэрэглэнэ — товчны нэр л өөр.
 * Барааны нэр, үнэ нь хэрэглэгч засдаггүй тул оролт биш, дэлгэрэнгүй болгож
 * харуулна (хүсэлтэд шаардлагатай тул нуусан талбараар үлдээв).
 */

const SaleForm = ({
  form,
  initialValues,
  onFinish,
  submitting = false,
  product = {},
  id,
  submitText = 'Хямдрал үүсгэх',
}) => {
  const title = Form.useWatch('title', form);
  const priceBeforeSale = Number(Form.useWatch('priceBeforeSale', form)) || 0;
  const saleValue = Number(Form.useWatch('saleValue', form)) || 0;

  // Зөв бөглөсөн үед л хямдралын хэмжээг доод мөрөнд харуулна
  const hasDiscount = priceBeforeSale > 0 && saleValue > 0 && saleValue < priceBeforeSale;
  const discountAmount = priceBeforeSale - saleValue;

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      autoComplete="off"
      onFinish={onFinish}
    >
      <Form.Item name="title" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="priceBeforeSale" hidden>
        <InputNumber />
      </Form.Item>

      <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
        <SectionCard icon={<BsBox2 />} title="Бараа">
          <Descriptions
            {...descriptionProps}
            column={{ xs: 1, sm: 3 }}
            items={[
              { key: 'title', label: 'Барааны нэр', children: val(title ?? product?.title) },

              {
                key: 'price',
                label: 'Үндсэн үнэ',
                children: priceBeforeSale ? formatCurrency(priceBeforeSale) : '-',
              },
            ]}
          />
        </SectionCard>

        <SectionCard icon={<MdOutlineLocalOffer />} title="Хямдралын мэдээлэл">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="saleValue"
                label="Хямдарсан үнэ"
                rules={[
                  { required: true, message: 'Хямдарсан үнэ оруулна уу!' },
                  ({ getFieldValue }) => ({
                    validator: (_, value) => {
                      if (value === null || value === undefined || value === '') {
                        return Promise.resolve();
                      }
                      if (Number(value) <= 0) {
                        return Promise.reject(new Error('Хямдарсан үнэ 0-с их байх ёстой!'));
                      }
                      const basePrice = Number(getFieldValue('priceBeforeSale')) || 0;
                      if (basePrice > 0 && Number(value) >= basePrice) {
                        return Promise.reject(
                          new Error(`Үндсэн үнэ (${formatCurrency(basePrice)})-с бага байх ёстой!`)
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber {...currencyInputProps} placeholder="Энд бич" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="dateRange"
                label="Хямдрал үргэлжлэх огноо"
                extra="Сонгосон дуусах өдрийн 23:59 хүртэл хямдрал үргэлжилнэ"
                rules={[{ required: true, message: 'Хямдрал үргэлжлэх огноо сонгоно уу!' }]}
              >
                <RangePicker style={{ width: '100%' }} disabledDate={disabledPastDate} />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="description"
                label="Тэмдэглэл"
                rules={[{ required: true, message: 'Тэмдэглэл оруулна уу!' }]}
                style={{ marginBottom: 0 }}
              >
                <TextArea rows={3} placeholder="Энд бич" />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        <FormFooter
          extra={
            hasDiscount ? (
              <Space size={8}>
                <Text type="secondary">Хямдралын хэмжээ</Text>
                <Text strong>{formatCurrency(discountAmount)}</Text>
              </Space>
            ) : null
          }
        >
          <Link to={{ pathname: `/merchant/products/${id}`, search: '?tab=sale' }}>
            <Button htmlType="button">Буцах</Button>
          </Link>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitText}
          </Button>
        </FormFooter>
      </Space>
    </Form>
  );
};

export default SaleForm;
